import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchPublicDashboardPresentation } from "../../api/client";
import { formatDashboardPeriodLabel } from "../../utils/dashboardDeepLink";
import Atendimento from "../../components/Atendimento";
import CustomerAwareness from "../../components/CustomerAwareness";
import Produtos from "../../components/Produtos";
import VendaProdutos from "../../components/VendaProdutos";
import VendaPassagens from "../../components/VendaPassagens";
import SurveyAnalysis from "../../components/SurveyAnalysis";

const TAB_RENDERERS = {
  overview: (props) => <Atendimento {...props} />,
  service: (props) => <Atendimento {...props} tipo_scope="relacionamento" />,
  clients: (props) => <CustomerAwareness {...props} />,
  products: (props) => <Produtos {...props} />,
  sales: (props) => <VendaProdutos {...props} />,
  busTickets: (props) => <VendaPassagens {...props} />,
  surveys: (props) => <SurveyAnalysis {...props} />,
};

function PresentationFooter() {
  const { t } = useTranslation("settings");
  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 2,
        textAlign: "center",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {t("dashboardPresentation.copyright", {
          defaultValue: "© {{year}} Xpro. Todos os direitos reservados.",
          year: new Date().getFullYear(),
        })}
      </Typography>
    </Box>
  );
}

export default function PublicDashboardPresentationPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation("settings");

  const { data: presentation, isLoading, error, isError } = useQuery(
    ["public-dashboard-presentation", token],
    () => fetchPublicDashboardPresentation(token),
    { enabled: Boolean(token), retry: false, staleTime: Infinity }
  );

  const expired = isError && error?.response?.status === 410;

  if (isLoading) {
    return (
      <Box className="portal-scroll-root" sx={{ height: { xs: "100dvh", sm: "100vh" } }}>
        <Box
          className="portal-scroll-main"
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100%" }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (expired || !presentation?.snapshot) {
    return (
      <Box className="portal-scroll-root" sx={{ height: { xs: "100dvh", sm: "100vh" } }}>
        <Box className="portal-scroll-main">
          <Container maxWidth="sm" sx={{ py: 8 }}>
            <Alert severity="warning" variant="outlined">
              <Typography variant="h6" gutterBottom>
                {t("dashboardPresentation.expiredTitle", { defaultValue: "Link expirado" })}
              </Typography>
              <Typography variant="body2">
                {t("dashboardPresentation.expiredBody", {
                  defaultValue:
                    "Este link de apresentação não está mais disponível. Peça um novo link a quem compartilhou o painel.",
                })}
              </Typography>
            </Alert>
          </Container>
          <PresentationFooter />
        </Box>
      </Box>
    );
  }

  if (error && !presentation) {
    return (
      <Box className="portal-scroll-root" sx={{ height: { xs: "100dvh", sm: "100vh" } }}>
        <Box className="portal-scroll-main">
          <Container maxWidth="sm" sx={{ py: 8 }}>
            <Alert severity="error">
              {error?.response?.data?.message ||
                t("dashboardPresentation.loadFailed", { defaultValue: "Não foi possível carregar a apresentação." })}
            </Alert>
          </Container>
          <PresentationFooter />
        </Box>
      </Box>
    );
  }

  const periodLabel = formatDashboardPeriodLabel(presentation.start, presentation.end, i18n.language);
  const expiresLabel = presentation.expiresAt
    ? new Date(presentation.expiresAt).toLocaleString(i18n.language)
    : null;
  const renderTab = TAB_RENDERERS[presentation.tab];

  const panelProps = {
    start: presentation.start,
    end: presentation.end,
    presentationSnapshot: presentation.snapshot,
    readOnly: true,
    surveyId: presentation.surveyId,
    surveyTitle: presentation.surveyTitle,
  };

  return (
    <Box
      className="portal-scroll-root"
      sx={{
        height: { xs: "100dvh", sm: "100vh" },
        bgcolor: "background.default",
      }}
    >
      <Box className="portal-scroll-main">
        <Paper
          elevation={0}
          square
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            borderBottom: 1,
            borderColor: "divider",
            py: 2,
            px: { xs: 2, md: 3 },
            bgcolor: "background.paper",
          }}
        >
          <Container maxWidth="xl">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {presentation.workspaceLabel ||
                    t("dashboardPresentation.brand", { defaultValue: "Apresentação Xpro" })}
                </Typography>
                <Typography variant="h5" component="h1">
                  {presentation.title || t("dashboardPage.title", { defaultValue: "Dashboard" })}
                </Typography>
                {periodLabel ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {periodLabel}
                    {presentation.surveyTitle ? ` · ${presentation.surveyTitle}` : ""}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={t("dashboardPresentation.readOnlyChip", { defaultValue: "Somente leitura" })}
                  color="default"
                  variant="outlined"
                />
                {expiresLabel ? (
                  <Chip
                    size="small"
                    label={t("dashboardPresentation.validUntilChip", {
                      defaultValue: "Válido até {{date}}",
                      date: expiresLabel,
                    })}
                    color="info"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
            </Stack>
          </Container>
        </Paper>

        <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, md: 2 }, pb: 6 }}>
          {renderTab ? (
            renderTab(panelProps)
          ) : (
            <Alert severity="warning">
              {t("dashboardPresentation.unsupportedTab", {
                defaultValue: "Esta aba não pode ser exibida na apresentação pública.",
              })}
            </Alert>
          )}
        </Container>
        <PresentationFooter />
      </Box>
    </Box>
  );
}
